<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  value: {
    type: Boolean,
    default: false
  },
  size: {
    type: Number,
    default: 24
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits([ 'update:value' ])

const isAnimating = ref(false)

const toggle = () => {
  if (props.disabled) return
  if (isAnimating.value) return

  const newValue = !props.value

  if (newValue) {
    isAnimating.value = true

    setTimeout(() => (isAnimating.value = false), 500)
  }

  emit('update:value', newValue)
}

const classes = computed(() => ({
  '-checked': props.value,
  '-is-animating': isAnimating.value,
  '-disabled': props.disabled,
}))
</script>

<template>
  <button
    type="button"
    class="c-heart-button"
    :class="classes"
    :style="{ width: size + 'px', height: size + 'px' }"
    :aria-disabled="disabled ? 'true' : 'false'"
    :disabled="disabled"
    @click="toggle"
  >
    <svg
      viewBox="0 0 35 35"
      class="heart heart-shape"
      :style="{ width: size + 'px', height: size + 'px' }"
    >
      <path d="M22.6063 6.00023C26.9963 6.00023 30 10.0977 30 13.9201C29.9538 21.6567 17.6987 28 17.4769 28C17.2551 28 5 21.6567 5 13.9201C5 10.0977 7.9575 6.00023 12.3937 6.00023C13.3632 6.00013 14.3208 6.21109 15.1991 6.61813C16.0774 7.02518 16.8549 7.61839 17.4769 8.35601C18.103 7.61145 18.8878 7.01407 19.7748 6.60663C20.6618 6.19928 21.6288 5.99219 22.6063 6.00023Z" />
    </svg>

    <svg
      v-for="i in 4"
      viewBox="0 0 35 35"
      :key="i"
      :class="`halo halo-${i}`"
      :style="{ width: size + 'px', height: size + 'px' }"
    >
      <path d="M17.4944 2.80988C19.7534 1.35415 22.4463 0.747502 25.1026 1.09604C31.0276 1.92459 35 7.81509 35 13.5807C35 24.2721 20.5354 33 17.4944 33C14.4534 33 0 24.2948 0 13.6034C0 7.80376 3.9724 1.91326 9.8974 1.11879C12.5476 0.761358 15.2375 1.36012 17.4944 2.80988Z" />
    </svg>
  </button>
</template>

<style lang="scss">
:root {
  --heart-fill: #ff4d8c;
  --heart-outline: rgba(18,30,72,0.10);
  --disabled-fill: #f2f2f4;
  --disabled-stroke: #e0e0e6;
  --heart-glow-color: rgba(255, 77, 140, 0.6);
}

.c-heart-button {
  all: unset;
  display: inline-grid;
  place-items: center;
  position: relative;
  cursor: pointer;
  border-radius: 8px;
  padding: 6px;
  user-select: none;
  transition: transform 150ms ease, box-shadow 160ms ease;
  background: transparent;

  & > .heart {
    transition: filter 0.2s ease-out;
    overflow: visible
  }

  & > .heart-shape {
    fill: none;
    stroke: var(--heart-outline);
    stroke-width: 1.2px;
    vector-effect: non-scaling-stroke;
    transition: all 180ms ease, filter 180ms ease;
  }

  & > .halo {
    fill: none;
    stroke: var(--heart-fill);
    opacity: 0;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  &.-is-animating {
    & > .heart {
      filter: drop-shadow(0px 0px 8px var(--heart-glow-color));
    }

    & > .halo-1 { animation: halo1 0.35s ease-out; }
    & > .halo-2 { animation: halo2 0.40s ease-out; }
    & > .halo-3 { animation: halo3 0.45s ease-out; }
    & > .halo-4 { animation: halo4 0.50s ease-out; }
  }

  &:not(.-disabled):hover {
    & > .heart-shape {
      stroke: var(--heart-fill);
      transform: scale(1.06);
      filter: drop-shadow(0px 0px 4px rgba(255,77,140,0.36));
    }
  }

  &:not(.-checked):not(.-disabled):hover {
    & > .heart {
      filter: drop-shadow(0px 0px 8px rgba(255, 77, 140, 0.5));
    }
  }

  &.-checked {
    & > .heart-shape {
      fill: var(--heart-fill);
      stroke: var(--heart-fill);
    }

    & > .heart {
      filter: drop-shadow(0px 10px 9px rgba(255, 77, 140, 0.5));
    }
  }

  &.-checked:not(.-disabled):hover {
    & > .heart-shape {
      fill: rgba(255, 77, 140, 0.7);
      stroke: var(--heart-fill);
      transform: scale(1.06);
      filter: drop-shadow(0px 0px 6px var(--heart-glow-color));
    }
  }

  &.-disabled {
    cursor: not-allowed;
    opacity: 0.55;

    & > .heart-shape {
      fill: var(--disabled-fill);
      stroke: var(--disabled-stroke);
    }

    & > .heart { filter: none; }
  }
}

@keyframes halo1 {
  0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
  50%  { opacity: 0.8; transform: translate(-50%, -50%) scale(0.95); }
  100%  { opacity: 0; transform: translate(-50%, -50%) scale(1.05); }
}

@keyframes halo2 {
  0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
  50%  { opacity: 0.6; transform: translate(-50%, -50%) scale(1.2); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(1.3); }
}

@keyframes halo3 {
  0%   { opacity: 0; transform: translate(-50%, -50%) scale(1.0); }
  50%  { opacity: 0.4; transform: translate(-50%, -50%) scale(1.5); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(1.6); }
}

@keyframes halo4 {
  0%   { opacity: 0; transform: translate(-50%, -50%) scale(1.1); }
  50%  { opacity: 0.2; transform: translate(-50%, -50%) scale(1.8); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(1.9); }
}

</style>
